/**
 * 🆕 [Phase 19B] Helpers ordonnancement BT par jour
 * =================================================
 * Phase 19B introduit `ordres_par_jour: { "YYYY-MM-DD": ordre }` sur bons_travail
 * et service_calls, pour permettre un ordre différent par jour d'un même BT.
 *
 * Pour la rétro-compatibilité, on fallback sur l'ancien champ scalaire
 * `ordre_journalier` si la clé date n'est pas présente. Cela permet aux
 * anciennes valeurs déjà saisies de continuer à s'afficher sur tous les
 * jours du BT jusqu'à ce qu'elles soient migrées explicitement (script
 * `migrate_ordre_journalier_par_jour.py`) ou réenregistrées via l'UI.
 *
 * Forward-compatible : la signature accepte un employee_id en 3e argument
 * pour permettre une future granularité par employé sans réécriture.
 */

/**
 * Retourne l'ordre journalier effectif d'un BT/SC pour une date donnée.
 * Retourne null si aucun ordre n'est défini (ni par jour, ni global).
 *
 * @param {object} bon - BT ou SC enrichi par le backend
 * @param {string} dateStr - 'YYYY-MM-DD'
 * @param {string} [_employeeId] - réservé pour usage futur (granularité par employé)
 * @returns {number|null}
 */
export function getOrdreEffectif(bon, dateStr, _employeeId) {
  if (!bon || !dateStr) return null;
  // 1) Champ enrichi par /collisions (préféré)
  if (bon.ordre_journalier_effectif != null && bon.ordre_journalier_effectif !== undefined) {
    // Si le backend a déjà calculé pour cette date (cas où on lit la réponse de /collisions),
    // on lui fait confiance.
    return bon.ordre_journalier_effectif;
  }
  // 2) Lecture directe du dict par jour
  const opj = bon.ordres_par_jour || {};
  if (opj[dateStr] != null) return opj[dateStr];
  // 3) Fallback scalaire global (BT non encore migrés)
  if (bon.ordre_journalier != null) return bon.ordre_journalier;
  return null;
}

/**
 * Renvoie l'ensemble des employee_id "concernés" par un BT pour une date donnée.
 * Aligné sur la logique backend `_bt_employe_concerne` + filtrage par
 * dates_assignees côté front (utile pour identifier qui doit voir "Job N").
 *
 * @param {object} bon - BT
 * @param {string} dateStr - 'YYYY-MM-DD'
 * @returns {Set<string>}
 */
export function getEmployesConcernesPourJour(bon, dateStr) {
  const ids = new Set();
  if (!bon || !dateStr) return ids;

  const exclus = new Set();
  (bon.employes_exclus || []).forEach((ee) => {
    const eid = typeof ee === 'string' ? ee : (ee?.employee_id || ee?.id);
    if (eid) exclus.add(eid);
  });

  const chefsParJour = bon.chefs_par_jour || {};
  const chefDuJour = chefsParJour[dateStr];

  // 1) Chef de la journée
  if (chefDuJour && chefDuJour !== '__none__') {
    ids.add(chefDuJour);
  } else if (bon.chef_equipe_id) {
    const cda = bon.chef_dates_assignees;
    if (Array.isArray(cda) && cda.length > 0) {
      if (cda.includes(dateStr)) ids.add(bon.chef_equipe_id);
    } else {
      ids.add(bon.chef_equipe_id);
    }
  }

  // 2) Employés assignés (avec filtre dates_assignees si renseigné)
  (bon.employes_assignes || []).forEach((ea) => {
    if (!ea || typeof ea !== 'object') return;
    const eid = ea.employee_id || ea.id;
    if (!eid) return;
    const da = ea.dates_assignees;
    if (Array.isArray(da) && da.length > 0) {
      if (!da.includes(dateStr)) return;
    }
    ids.add(eid);
  });

  // 3) Retirer les exclus
  exclus.forEach((e) => ids.delete(e));
  return ids;
}
